import React, { memo, useEffect, useState } from "react";
// import Chart from "react-apexcharts";
import { Chart } from "react-google-charts";
import {
  CustomInput,
  Input,
  Label,
  Tooltip,
  UncontrolledTooltip,
} from "reactstrap";
import {
  countTrueValues,
  deepClone,
  formatDate,
} from "../../../../helper-methods";
import SvgIcons from "../SvgIcons";

const checkboxLabel = [
  {
    id: "1",
    label: "Total Clicks",
    value: "0",
    content:
      "Total clicks is how many times a user clicked through to your site. How this is counted depends on the search result type.",
    key: "totalClicks",
    graphKey: "clicks",
  },
  {
    id: "2",
    label: "Total Impressions",
    value: "0",
    content:
      "Total impressions is how many times a user saw a link to your site in search results. This is calculated differently for images and other search result types, depending on whether or not the result was scrolled into view.",
    key: "totalImpressions",
    graphKey: "impressions",
  },
  {
    id: "3",
    label: "Average CTR",
    value: "0",
    content:
      "Average CTR is the percentage of impressions that resulted in a click.",
    key: "totalCtr",
    graphKey: "ctr",
  },
  {
    id: "4",
    label: "Average Position",
    value: "0",
    content:
      "Average position is the average position of your site in search results, based on its highest position whenever it appeared in a search. Individual page position is available in the table below the chart. Position determination can be complicated by features such as carousels or Knowledge Panels.",
    key: "totalPosition",
    graphKey: "position",
  },
];

const defaultLineGraphColor = ["#57aaff", "#8350ff", "#2bcac3", "#ff9360"];

const defaultGraphKeys = ["clicks", "impressions", "ctr", "position"];

const ConsoleLineGraph = ({ data, onClickCard }) => {
  const [options, setOptions] = useState();
  const [chartData, setChartData] = useState([]);
  const [checkboxLabels, setCheckboxLabels] = useState(checkboxLabel);
  const [isChecked, setIsChecked] = useState([true, true, true, true]);

  const trueCount = countTrueValues(isChecked);

  const _handleCheckboxChange = (checked = false, index = 0) => {
    const newIsChecked = deepClone(isChecked);
    newIsChecked[index] = checked;
    setIsChecked(newIsChecked);

    const filteredArray = checkboxLabels.filter(
      (obj, index) => newIsChecked[index]
    );

    const valuesArray = filteredArray.map((obj) => obj.graphKey);

    _setChartState(data, valuesArray);
    onClickCard(valuesArray);
  };

  const _setChartState = (statsData, graphKeys) => {
    // console.log({ statsData });
    // console.log({ graphKeys });

    const updatedKeys = ["keys"].concat(graphKeys);
    const newDataSet = statsData.map((obj) => {
      const arr = updatedKeys.map((key) => {
        return key === "keys" ? formatDate(obj[key][0], true) : obj[key];
      });
      return arr;
    });

    const data = [["Date"].concat(graphKeys), ...newDataSet];

    // console.log("graph Data >>", data);

    // Set chart options
    const options = {
      title: "Search Performance",
      hAxis: {
        title: "Date",
        format: "MM/dd/yy", // Date format for the horizontal axis
        slantedText: true, // Slant the text to avoid overlap
        slantedTextAngle: 45, // Angle for the slanted text
        showTextEvery: 1,
        gridlines: { color: "transparent" },
        minValue: new Date(data[1][0]),
      },
      vAxes: {
        0: {
          title: null,
          gridlines: { color: "transparent" },
          textPosition: "none",
        },
        1: {
          title: null,
          gridlines: { color: "transparent" },
          textPosition: "none",
        },
        2: {
          title: null,
          gridlines: { color: "transparent" },
          textPosition: "none",
        },
        3: {
          title: null,
          gridlines: { color: "transparent" },
          textPosition: "none",
        },
      },
      seriesType: "line",
      series: { 5: { type: "line" } },
      tooltip: { isHtml: true, trigger: "focus" },
      curveType: "smooth",
      colors: defaultLineGraphColor,
    };

    setChartData(data);
    setOptions(options);
  };

  const _setCardInfo = (data) => {
    let totalClicks = 0;
    let totalImpressions = 0;
    let totalCtr = 0;
    let totalPosition = 0;

    data.forEach((entry) => {
      totalClicks += entry.clicks;
      totalImpressions += entry.impressions;
      totalCtr += entry.ctr;
      totalPosition += entry.position;
    });

    let numberOfEntriesForPosition =
      data?.length && data.filter((each) => each.position > 0).length;

    let averageCtr = (totalClicks / totalImpressions) * 100;
    let averagePosition = (totalPosition / numberOfEntriesForPosition).toFixed(
      2
    );

    const updatedCheckboxLabels = checkboxLabel.map((item) => {
      switch (item.key) {
        case "totalClicks":
          return { ...item, value: totalClicks.toString() };
        case "totalImpressions":
          return { ...item, value: totalImpressions.toString() };
        case "totalCtr":
          return { ...item, value: averageCtr.toFixed(2) + "%" };
        case "totalPosition":
          return { ...item, value: averagePosition };
        default:
          return item;
      }
    });
    setCheckboxLabels(updatedCheckboxLabels);
  };

  useEffect(() => {
    console.log("data >>", data);
    if (data?.length) {
      _setChartState(data, defaultGraphKeys);
      _setCardInfo(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <div className="chartTools">
        {checkboxLabels.map((each, index) => (
          <>
            <Label
              className={`chartInfo ${isChecked[index] ? "selected" : ""}`}
            >
              <CustomInput
                type="checkbox"
                value={index}
                id={each.id}
                checked={isChecked[index]}
                onChange={(e) => _handleCheckboxChange(e.target.checked, index)}
                label={each.label}
                disabled={trueCount === 1 && isChecked[index]}
              />

              <h3> {each.value}</h3>
              <div className="help" id={`TooltipExample${each.id}`}>
                <SvgIcons type={"circleHelp"} />
              </div>
            </Label>

            <UncontrolledTooltip
              placement="bottom"
              target={`TooltipExample${each.id}`}
            >
              {each.content}
            </UncontrolledTooltip>
          </>
        ))}
      </div>
      <Chart
        options={options}
        data={chartData}
        chartType="ComboChart"
        height="550px"
        width="auto"
      />
    </>
  );
};

export default memo(ConsoleLineGraph);
